import { create } from "zustand";
import { isWorkshopBffEnabled } from "@/env";
import { listDocs } from "@/lib/docs-api";
import type { SyncState } from "@/stores/synced-stores";

/**
 * E2 — changed-since coordinator for the per-workshop register stores.
 *
 * The seven register stores (findings / decisions / actions / assumptions /
 * opportunities / recommendations / facilitation-notes) were deliberately kept
 * OFF the 15s poll: each one's `sync()` re-lists all eight workshop buckets, so
 * polling seven of them would cost ~56 list calls per tick against Azure Blob
 * (see `synced-stores.ts`). This coordinator makes them live for the price of
 * **one** list call per tick:
 *
 *   1. A single tier-wide `GET /api/docs/workshops/list?since=<high-water>`
 *      returns only blobs modified since the last tick (server-filtered, E2).
 *   2. The changed paths are fanned out to every register store's
 *      `ingestRemoteChanges()`, which GETs only the records of its own type and
 *      merges them in (reusing the E1 etag + merge plumbing).
 *   3. The high-water mark advances to the newest `last_modified` we saw, so the
 *      next tick only asks for what changed after it. Using the server's own
 *      timestamps (not the client clock) keeps it immune to clock skew.
 *
 * A `since` list can't report deletions (a deleted blob simply stops appearing),
 * so every {@link FULL_RECONCILE_EVERY}th tick falls back to each store's full
 * `sync()` — which re-lists and prunes — to converge deletes. This is the one
 * registered `SyncableStore` that stands in for all seven; the stores
 * themselves stay OUT of `SYNCED_STORES`.
 *
 * IDB-only / offline: inert (`disabled`) — there's no shared backend to poll.
 */

const WORKSHOPS_TIER = "workshops";

/** Full-reconcile cadence (in ticks) to catch deletes a `since` list misses.
 *  20 ticks × 15s ≈ every 5 minutes. */
export const FULL_RECONCILE_EVERY = 20;

/** The slice of a register store this coordinator drives. Structurally a subset
 *  of `WorkshopRecordStore`, so any factory store satisfies it. */
export type RegisterStoreLike = {
  getState: () => {
    ingestRemoteChanges: (changedPaths: string[]) => Promise<number>;
    sync: () => Promise<void>;
  };
};

type RegisterSyncState = {
  syncState: SyncState;
  lastSyncedAt: number | null;
  syncError: string | null;
  /** ISO high-water mark — the newest `last_modified` ingested so far. */
  since: string | null;
  /** Tick counter, for the periodic full reconcile. */
  tick: number;
  sync: () => Promise<void>;
};

/**
 * The register stores this coordinator drives. Injected (not imported) so the
 * coordinator stays decoupled + unit-testable; `synced-stores.ts` wires the
 * real seven in at module load via {@link registerSyncStores}.
 */
let REGISTERED: RegisterStoreLike[] = [];

/** Wire the real register stores in (called once from `synced-stores.ts`). */
export function registerSyncStores(stores: RegisterStoreLike[]): void {
  REGISTERED = stores;
}

/** Newest of two ISO timestamps (string compare is correct for ISO-8601 in the
 *  same offset; the BFF emits UTC `.isoformat()`, so it's monotonic). `null`
 *  sorts before any real timestamp. */
function maxIso(a: string | null, b: string | null): string | null {
  if (!a) return b;
  if (!b) return a;
  return b > a ? b : a;
}

export const useRegisterSync = create<RegisterSyncState>((set, get) => ({
  syncState: isWorkshopBffEnabled() ? "idle" : "disabled",
  lastSyncedAt: null,
  syncError: null,
  since: null,
  tick: 0,
  sync: async () => {
    if (!isWorkshopBffEnabled()) {
      if (get().syncState !== "disabled") set({ syncState: "disabled" });
      return;
    }
    const tick = get().tick + 1;
    set({ tick, syncState: "syncing", syncError: null });

    try {
      // Periodic full reconcile (catches deletes a `since` list can't surface).
      // Also the first tick (since === null): seed the high-water mark from a
      // full list without forcing every store to re-GET — the stores already
      // hydrated their own records on open.
      const sinceMark = get().since;
      const fullReconcile = sinceMark === null || tick % FULL_RECONCILE_EVERY === 0;

      if (fullReconcile) {
        // Re-list everything to (a) prune deletes via each store's own sync and
        // (b) re-establish the high-water mark from the current truth.
        const all = await listDocs(WORKSHOPS_TIER, "");
        if (sinceMark !== null) {
          // Only run the (heavier) per-store full sync on the periodic pass, not
          // on first-tick seeding — first tick just needs the watermark.
          await Promise.all(REGISTERED.map((s) => s.getState().sync()));
        }
        const newest = all.reduce<string | null>(
          (acc, b) => maxIso(acc, b.last_modified ?? null),
          sinceMark,
        );
        set({
          since: newest,
          syncState: "synced",
          lastSyncedAt: Date.now(),
          syncError: null,
        });
        return;
      }

      // Cheap changed-since path: one list, fan the changed paths to the stores.
      const changed = await listDocs(WORKSHOPS_TIER, "", sinceMark);
      if (changed.length > 0) {
        const paths = changed.map((b) => b.path);
        await Promise.all(
          REGISTERED.map((s) => s.getState().ingestRemoteChanges(paths)),
        );
      }
      const newest = changed.reduce<string | null>(
        (acc, b) => maxIso(acc, b.last_modified ?? null),
        sinceMark,
      );
      set({
        since: newest,
        syncState: "synced",
        lastSyncedAt: Date.now(),
        syncError: null,
      });
    } catch {
      set({ syncState: "error", syncError: "Couldn’t reach the records service." });
    }
  },
}));
